/** Browser-side code diagnostics mapped to Monaco marker severities. */

export type CodeDiagnosticSeverity = 'error' | 'warning' | 'info' | 'hint';

export type CodeDiagnostic = {
  severity: CodeDiagnosticSeverity;
  message: string;
  /** 1-based line */
  line: number;
  /** 1-based column */
  column: number;
  source: string;
};

/** Monaco MarkerSeverity numeric values (avoid importing monaco in pure helpers). */
export const MONACO_MARKER_SEVERITY = {
  Hint: 1,
  Info: 2,
  Warning: 4,
  Error: 8
} as const;

export function toMonacoMarkerSeverity(severity: CodeDiagnosticSeverity): number {
  switch (severity) {
    case 'error':
      return MONACO_MARKER_SEVERITY.Error;
    case 'warning':
      return MONACO_MARKER_SEVERITY.Warning;
    case 'info':
      return MONACO_MARKER_SEVERITY.Info;
    case 'hint':
      return MONACO_MARKER_SEVERITY.Hint;
  }
}

export function validateCodeContent(languageId: string, text: string): CodeDiagnostic[] {
  switch (languageId) {
    case 'json':
      return validateJson(text);
    case 'xml':
      return validateXml(text);
    case 'yaml':
      return validateYamlBasic(text);
    default:
      return [];
  }
}

export function validateJson(text: string): CodeDiagnostic[] {
  const trimmed = text.trim();
  if (!trimmed) return [];

  try {
    JSON.parse(text);
    return [];
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Invalid JSON.';
    const pos = positionFromJsonError(message, text);
    return [
      {
        severity: 'error',
        message,
        line: pos.line,
        column: pos.column,
        source: 'json'
      }
    ];
  }
}

export function validateXml(text: string): CodeDiagnostic[] {
  const trimmed = text.trim();
  if (!trimmed) return [];

  if (typeof DOMParser === 'undefined') {
    return [];
  }

  const doc = new DOMParser().parseFromString(trimmed, 'application/xml');
  const parserError = doc.querySelector('parsererror');
  if (!parserError) return [];

  const message = (parserError.textContent || 'Invalid XML.').trim().split('\n')[0] || 'Invalid XML.';
  return [
    {
      severity: 'error',
      message,
      line: 1,
      column: 1,
      source: 'xml'
    }
  ];
}

/**
 * Lightweight YAML checks without a parser dependency.
 * Empty input is valid; tabs mid-line and odd unclosed quotes are reported.
 */
export function validateYamlBasic(text: string): CodeDiagnostic[] {
  if (!text.trim()) return [];

  const diagnostics: CodeDiagnostic[] = [];
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? '';
    const lineNo = i + 1;

    if (/[^\s\t].*\t|\t.*[^\s\t]/.test(line.replace(/^\s+/, ''))) {
      diagnostics.push({
        severity: 'warning',
        message: 'YAML prefers spaces; unexpected tab in line content.',
        line: lineNo,
        column: Math.max(1, line.indexOf('\t') + 1),
        source: 'yaml'
      });
    }

    const hash = line.indexOf('#');
    const code = hash >= 0 ? line.slice(0, hash) : line;
    if (hasOddUnescapedQuotes(code, '"') || hasOddUnescapedQuotes(code, "'")) {
      diagnostics.push({
        severity: 'warning',
        message: 'Possible unclosed quoted string.',
        line: lineNo,
        column: 1,
        source: 'yaml'
      });
    }
  }

  return diagnostics;
}

function hasOddUnescapedQuotes(text: string, quote: '"' | "'"): boolean {
  let count = 0;
  for (let i = 0; i < text.length; i++) {
    if (text[i] !== quote) continue;
    if (i > 0 && text[i - 1] === '\\') continue;
    count += 1;
  }
  return count % 2 === 1;
}

function positionFromJsonError(message: string, text: string): { line: number; column: number } {
  const match = /position\s+(\d+)/i.exec(message);
  if (!match) return { line: 1, column: 1 };
  const offset = Number(match[1]);
  if (!Number.isFinite(offset) || offset < 0) return { line: 1, column: 1 };

  let line = 1;
  let column = 1;
  const end = Math.min(offset, text.length);
  for (let i = 0; i < end; i++) {
    if (text[i] === '\n') {
      line += 1;
      column = 1;
    } else {
      column += 1;
    }
  }
  return { line, column };
}
